// src/components/forms/variants/gated/build-gated-resource-template.ts

import type { EmailTemplateConfig } from "~/components/forms/base/types"
import type { ResourceDetails } from "~/components/forms/variants/gated/types"

/**
 * Canonical CAN-SPAM legal entity + Delaware postal address.
 *
 * These MUST match the values pinned in
 * `src/lib/legal/__tests__/entity-consistency.test.ts`. They are the fallback
 * when the `NEXT_PUBLIC_COMPANY_*` env vars are unset so the gated-resource
 * delivery email always carries the required entity name and physical mailing
 * address in its footer.
 */
export const CANONICAL_COMPANY_NAME = "AdSights Inc."
export const CANONICAL_COMPANY_ADDRESS =
  "254 Chapman Rd, Ste 208 #28056, Newark, Delaware 19702"

/**
 * Arguments for {@link buildGatedResourceTemplate}.
 */
export interface BuildGatedResourceTemplateArgs {
  /** Caller-supplied template overrides (highest priority except CAN-SPAM identity). */
  customTemplate?: EmailTemplateConfig
  /** The resource being delivered. */
  resourceDetails: ResourceDetails
  /** Preheader / description copy (falls back to `Access <title>`). */
  descriptionText?: string
}

/**
 * Pure builder for the gated-resource delivery email template model.
 *
 * Extracted from `GatedContentEmailCapture` so the CAN-SPAM footer defaults
 * (entity + physical address) can be pinned by a unit test without rendering
 * the client component. The `company_name`/`company_address` defaults are
 * re-applied AFTER the caller `base`/`gatedResource` spreads so a stale caller
 * value can never blank or override the legally-required identity.
 */
export function buildGatedResourceTemplate({
  customTemplate,
  resourceDetails,
  descriptionText,
}: BuildGatedResourceTemplateArgs): EmailTemplateConfig {
  return {
    ...customTemplate,
    templateAlias: customTemplate?.templateAlias ?? "gated-resource-1",
    to: "", // This will be set by BaseEmailCaptureForm
    from:
      customTemplate?.from ??
      process.env.NEXT_PUBLIC_EMAIL_FROM ??
      "noreply@adsights.ai",
    templateModel: {
      base: {
        name: "", // Will be set with email prefix
        logo_url: process.env.NEXT_PUBLIC_LOGO_URL ?? "/logo-square.png",
        product_name: process.env.NEXT_PUBLIC_PRODUCT_NAME ?? "AdSights",
        product_url:
          process.env.NEXT_PUBLIC_PRODUCT_URL ?? "https://adsights.ai",
        support_email:
          process.env.NEXT_PUBLIC_SUPPORT_EMAIL ?? "support@adsights.ai",
        sender_name: process.env.NEXT_PUBLIC_SENDER_NAME ?? "AdSights Team",
        ...customTemplate?.templateModel?.base,
        // Re-apply CAN-SPAM identity AFTER the base spread so a stale caller
        // `base` cannot blank/override the required entity + physical address.
        company_name:
          process.env.NEXT_PUBLIC_COMPANY_NAME ?? CANONICAL_COMPANY_NAME,
        company_address:
          process.env.NEXT_PUBLIC_COMPANY_ADDRESS ?? CANONICAL_COMPANY_ADDRESS,
      },
      gatedResource: {
        // First spread base template model since GatedResourceTemplateModel extends it
        ...customTemplate?.templateModel?.base,
        // Then add gated resource specific fields
        preheader: descriptionText ?? `Access ${resourceDetails.title}`,
        resource_icon_url: resourceDetails.previewImage ?? "",
        resource_name: resourceDetails.title ?? "Resource",
        resource_link:
          customTemplate?.templateModel?.gatedResource?.resource_link ?? "#",
        resource_description: resourceDetails.description ?? "",
        // Override with any custom gated resource fields
        ...customTemplate?.templateModel?.gatedResource,
        // Ensure base fields are present in gated resource model
        logo_url: process.env.NEXT_PUBLIC_LOGO_URL ?? "/logo-square.png",
        product_name: process.env.NEXT_PUBLIC_PRODUCT_NAME ?? "AdSights",
        product_url:
          process.env.NEXT_PUBLIC_PRODUCT_URL ?? "https://adsights.ai",
        support_email:
          process.env.NEXT_PUBLIC_SUPPORT_EMAIL ?? "support@adsights.ai",
        sender_name: process.env.NEXT_PUBLIC_SENDER_NAME ?? "AdSights Team",
        company_name:
          process.env.NEXT_PUBLIC_COMPANY_NAME ?? CANONICAL_COMPANY_NAME,
        company_address:
          process.env.NEXT_PUBLIC_COMPANY_ADDRESS ?? CANONICAL_COMPANY_ADDRESS,
        // Optional fields
        related_resource_1_link:
          customTemplate?.templateModel?.gatedResource?.related_resource_1_link,
        related_resource_1_name:
          customTemplate?.templateModel?.gatedResource?.related_resource_1_name,
        related_resource_2_link:
          customTemplate?.templateModel?.gatedResource?.related_resource_2_link,
        related_resource_2_name:
          customTemplate?.templateModel?.gatedResource?.related_resource_2_name,
        twitter_share_url:
          customTemplate?.templateModel?.gatedResource?.twitter_share_url,
        linkedin_share_url:
          customTemplate?.templateModel?.gatedResource?.linkedin_share_url,
      },
    },
  }
}
